import { nanoid } from '@reduxjs/toolkit'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DEFAULT_LANGUAGES } from '../../config'
import { useT } from '../../i18n'
import { addLanguage, addTopping, updateTopping } from '../../redux/actions'
import { StoreState } from '../../redux/reducers'
import { Topping } from '../../redux/reducers/topping.reducer'
import { AppDispatch } from '../../redux/store'
import { toString } from '../../utils'
import { ToastType, useToast } from '../ToastProvider'
import ModalWithHeader from '../modals/ModalWithHeader'
import SelectableTileSection from '../sections/SelectableTileSection'
import ResponsiveFormInput from '../utils/ResponsiveFormInput'

interface ToppingFormProps {
    onClose?: () => void
    id?: string | null
}

interface Name {
    language: string
    name: string
    errorText: string
    id: string
}

interface Type {
    language: string
    type: string
    errorText: string
    id: string
}

const ToppingForm = (props: ToppingFormProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const toast = useToast()
    const t = useT()

    const languages = useSelector<StoreState, string[]>(state => state.languages)
    const topping = useSelector<StoreState, Topping | null>(state => props.id ? state.toppings.find(t => t.id === props.id) || null : null)

    const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>(DEFAULT_LANGUAGES)

    const [names, setNames] = React.useState<Name[]>([])
    const [types, setTypes] = React.useState<Type[]>([])

    const [loading, setLoading] = React.useState<boolean>(false)

    const languageClickHandler = (lng?: string) => {
        if (lng && lng !== '') {
            setSelectedLanguages(prev => {
                return prev.some(p => p === lng)
                    ? prev.length > 1
                        ? prev.filter(p => p !== lng)
                        : prev
                    : [
                        ...prev,
                        lng
                    ]
            })

            setNames(prev => {
                return prev.some(p => p.language === lng)
                    ? prev.length > 1
                        ? prev.filter(p => p.language !== lng)
                        : prev
                    : [
                        ...prev,
                        {
                            language: lng,
                            name: '',
                            errorText: '',
                            id: nanoid()
                        }
                    ]
            })
        }
    }

    const addLanguageClickHandler = (language: string) => {
        dispatch(addLanguage(language))
    }

    const nameChangeHandler = (name: Name, value: string) => {
        setNames(prev => {
            const names = [...prev]
            const index = names.findIndex(p => p.id === name.id)

            if (index > -1) {
                names[index] = {
                    ...names[index],
                    name: value,
                    errorText: ''
                }
            }

            return names
        })
    }

    const typeChangeHandler = (type: Type, value: string) => {
        setTypes(prev => {
            const types = [...prev]
            const index = types.findIndex(p => p.id === type.id)

            if (index > -1) {
                types[index] = {
                    ...types[index],
                    type: value,
                    errorText: ''
                }
            }

            return types
        })
    }

    const submitHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        e.preventDefault()

        let error = false

        names.forEach((name, i) => {
            if (toString(name.name) === '') {
                error = true
                setNames(prev => {
                    const names = [...prev]
                    names[i].errorText = name.language.charAt(0).toUpperCase() + name.language.substring(1) + t('Name required')
                    return names
                })
            }
        })

        types.forEach((type, i) => {
            if (toString(type.type) === '') {
                error = true
                setTypes(prev => {
                    const types = [...prev]
                    types[i].errorText = type.language.charAt(0).toUpperCase() + type.language.substring(1) + t('Type required')
                    return types
                })
            }
        })

        if (!error) {
            if (props.id && topping) {
                setLoading(true)
                dispatch(updateTopping({
                    names: names.map(n => ({ language: toString(n.language), name: toString(n.name) })),
                    types: types.map(n => ({ language: toString(n.language), type: toString(n.type) }))
                }, topping.id)).then(() => {
                    toast(t('Topping updated'))
                    props.onClose?.()
                }).catch(text => {
                    toast(text, ToastType.ERROR)
                }).finally(() => {
                    setLoading(false)
                })
            } else {
                setLoading(true)
                dispatch(addTopping({
                    names: names.map(n => ({ language: toString(n.language), name: toString(n.name) })),
                    types: types.map(n => ({ language: toString(n.language), type: toString(n.type) }))
                })).then(() => {
                    toast(t('Topping added'))
                    props.onClose?.()
                }).catch(text => {
                    toast(text, ToastType.ERROR)
                }).finally(() => {
                    setLoading(false)
                })
            }
        }
    }

    React.useEffect(() => {
        setNames(selectedLanguages.map(lng => {
            return {
                errorText: '',
                id: nanoid(),
                language: lng,
                name: ''
            }
        }))

        setTypes(selectedLanguages.map(lng => {
            return {
                errorText: '',
                id: nanoid(),
                language: lng,
                type: ''
            }
        }))
    }, [])

    React.useEffect(() => {
        if (topping && props.id) {
            setSelectedLanguages(() => {
                const lngs: string[] = []
                topping.names.forEach(n => {
                    if (lngs.indexOf(n.language) === -1) {
                        lngs.push(n.language)
                    }
                })
                return lngs
            })
            setNames(() => {
                const names: Name[] = []

                topping.names.forEach(n => {
                    if (!names.some(p => p.language === n.language)) {
                        names.push({
                            errorText: '',
                            id: n.id,
                            language: n.language,
                            name: n.name
                        })
                    }
                })

                return names
            })
            setTypes(() => {
                const types: Type[] = []

                topping.types.forEach(t => {
                    if (!types.some(p => p.language === t.language)) {
                        types.push({
                            errorText: '',
                            id: t.id,
                            language: t.language,
                            type: t.type
                        })
                    }
                })

                return types
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topping])

    return <ModalWithHeader
        buttonText={t('Save')}
        headerText={props.id ? t('Edit Topping') : t('Add Topping')}
        loading={loading}
        onClose={props.onClose}
        onSave={submitHandler}
        small
    >
        <div className='row mb-4'>
            <div className='col'>
                <SelectableTileSection
                    list={languages}
                    getSelectableTileProps={language => ({
                        key: language,
                        active: selectedLanguages.some(slng => slng === language),
                        onClick: languageClickHandler,
                        value: language,
                        label: language
                    })}
                    title={t('Language')}
                    instantInput={{
                        onAdd: addLanguageClickHandler,
                        label: t('Language')
                    }}
                />
            </div>
        </div>

        <div className='row'>
            <div className='col'>
                <div className='row'>
                    {names.map(n => {
                        return <ResponsiveFormInput
                            responsiveClassName='col-12'
                            containerClass='mb-4'
                            type='text'
                            key={n.id}
                            label={n.language.charAt(0).toUpperCase() + n.language.slice(1) + t('Name')}
                            value={n.name}
                            onChange={e => nameChangeHandler(n, e.target.value)}
                            errorText={n.errorText}
                        />
                    })}
                </div>
            </div>
        </div>

        <div className='row'>
            <div className='col'>
                <div className='row'>
                    {types.map((t, i) => {
                        return <ResponsiveFormInput
                            responsiveClassName='col-12'
                            containerClass={types.length - 1 !== i ? 'mb-4' : ''}
                            type='text'
                            key={t.id}
                            label={t.language.charAt(0).toUpperCase() + t.language.slice(1) + ' type'}
                            value={t.type}
                            onChange={e => typeChangeHandler(t, e.target.value)}
                            errorText={t.errorText}
                        />
                    })}
                </div>
            </div>
        </div>
    </ModalWithHeader>
}

export default ToppingForm